import React, { useEffect, useState } from 'react';
import DEFAULT_CONTENT from '../content/PAGE_CONTENT.json';
import '../styles/tokens.css';
import CountdownBar from '../components/CountdownBar';
import ImageSection from '../components/ImageSection';
import Testimonials from '../components/Testimonials';
import ValueStack from '../components/ValueStack';
import RequestForm from '../components/RequestForm';
import Offers from '../components/Offers';
import LegalFooter from '../components/LegalFooter';

/**
 * SalesPage — Advisors Edge · The Performance Reset.
 * All copy comes from PAGE_CONTENT.json (bundled fallback) or the `content`
 * prop (Stage 2: GET /api/page/resolve?slug=…). No secrets here; every
 * integration reads process.env.REACT_APP_* and is off when blank.
 *
 * variant="full" | "ads"
 *   ads → ads_safe testimonials, no italic/script accent font, extended legal footer.
 */

const BG = 'var(--bg)';
const ALT = 'var(--bg-alt)';

function useExpired(deadlineIso) {
  const [expired, setExpired] = useState(() => deadlineIso ? Date.now() >= new Date(deadlineIso).getTime() : false);
  useEffect(() => {
    if (!deadlineIso) return undefined;
    const id = setInterval(() => setExpired(Date.now() >= new Date(deadlineIso).getTime()), 1000);
    return () => clearInterval(id);
  }, [deadlineIso]);
  return expired;
}

const Eyebrow = ({ children }) => (children ? <p className="eyebrow">{children}</p> : null);

const Paras = ({ text }) => (
  <div className="body">
    {(text || '').split('\n\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
  </div>
);

function withAccent(text, accent) {
  if (!accent || !text || !text.includes(accent)) return text;
  const [before, after] = text.split(accent);
  return <>{before}<span className="acc">{accent}</span>{after}</>;
}

const HeadlineWithAccent = ({ text, accent }) => (
  <h1 className="display" data-testid="hero-h1">{withAccent(text, accent)}</h1>
);

const HeroKicker = ({ text, accent }) => (
  text ? <p className="hero-kicker" data-testid="hero-kicker">{withAccent(text, accent)}</p> : null
);

export default function SalesPage({ content: contentProp, variant = 'full', arm = '' }) {
  const c = contentProp || DEFAULT_CONTENT;
  const isAds = variant === 'ads';
  const P = c.pricing;
  const expired = useExpired(P.deadline_iso);
  const [openFaq, setOpenFaq] = useState(null);

  // Every public CTA scrolls to the request form. Checkout is never linked publicly.
  const href = '#request';

  useEffect(() => {
    // Internal analytics ping — fire-and-forget, never blocks render.
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      fetch(`${base}/api/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: window.location.pathname, variant, referrer: document.referrer || null }),
      }).catch(() => {});
    } catch (e) { /* ignore */ }
  }, [variant]);

  const CTA = ({ label, testId }) => (
    <a className="cta" data-testid={testId} href={href}>
      {label} <span aria-hidden="true">→</span>
    </a>
  );

  const h = c.hero;
  const logo = c.meta.logo
    ? <img className="logo" src={c.meta.logo} alt={c.meta.product_name} />
    : <span className="wordmark">{c.meta.brand}</span>;

  const sectionBg = [ALT, BG, ALT];
  const vs = c.value_stack;
  const inc = c.included;
  const en = c.enrollment;
  const sj = c.single_journey;
  const price = expired ? P.post_deadline_price : (P.discount_price ?? P.full_price);
  const le = c.live_event;
  const fc = c.final_cta;

  return (
    <div className={isAds ? 'variant-ads' : 'variant-full'} style={{ background: 'var(--bg)', color: 'var(--cream)' }} data-testid="sales-page">
      <CountdownBar
        deadline={P.deadline_iso}
        checkoutUrl={href}
        text={c.hello_bar.text}
        expiredText={c.hello_bar.expired_text}
      />

      {/* HERO — image bleeds left, logo overlaid top-left */}
      <ImageSection
        side={h.image_side || 'left'}
        image={`/brand/${h.image_slot}.webp`}
        slot={h.image_slot}
        background={BG}
        className="hero"
        eager
        testId="hero"
      >
        <div className="hero-logo" data-testid="hero-logo">{logo}</div>
        <HeroKicker text={h.eyebrow} accent={h.eyebrow_accent} />
        <HeadlineWithAccent text={h.h1} accent={h.h1_accent} />
        <p className="sub hero-subhead" data-testid="hero-subhead">{h.subhead}</p>
        {h.body && <Paras text={h.body} />}
        <div className="cta-wrap">
          <CTA label={h.cta} testId="cta-hero" />
          {!expired && <p className="cta-note">{h.cta_note}</p>}
        </div>
        {h.trust_line && <p className="trust">{h.trust_line}</p>}
      </ImageSection>

      {/* CHOOSE YOUR RESET — three offers, four-week featured */}
      <Offers offers={c.offers} />

      {/* WHAT / WHO / WHY */}
      {c.sections.map((s, i) => (
        <ImageSection
          key={s.key}
          side={s.image_side || 'left'}
          image={s.image_slot ? `/brand/${s.image_slot}.webp` : null}
          slot={s.image_slot || ''}
          background={sectionBg[i % sectionBg.length]}
          testId={`section-${s.key}`}
        >
          <Eyebrow>{s.eyebrow}</Eyebrow>
          <h2 className="display">{s.h2}</h2>
          <Paras text={s.body} />
          {s.themes?.length > 0 && (
            <div className="themes" data-testid={`themes-${s.key}`}>
              {s.themes.map((t) => (
                <div key={t.title} className="theme">
                  <p className="mt">{t.title}</p>
                  <p className="theme-lead">{t.lead}</p>
                  <p className="md">{t.text}</p>
                </div>
              ))}
            </div>
          )}
          {s.citation && (
            <p className="cite">Source: <a href={s.citation.url} target="_blank" rel="noopener noreferrer">{s.citation.text}</a></p>
          )}
          {s.bullets?.length > 0 && (
            <ul className="checks">
              {s.bullets.map((b) => <li key={b}><span className="chk" aria-hidden="true">✓</span>{b}</li>)}
            </ul>
          )}
          {s.contrast && (
            <div className="contrast">
              <div className="old">
                <p className="label">{s.contrast.old_way_label || 'The old way'}</p>
                <ul>{s.contrast.old_way.map((x) => <li key={x}>{x}</li>)}</ul>
              </div>
              <div className="new">
                <p className="label">{s.contrast.new_way_label || 'The new way'}</p>
                <ul>{s.contrast.new_way.map((x) => <li key={x}>{x}</li>)}</ul>
              </div>
            </div>
          )}
        </ImageSection>
      ))}

      {/* CURRICULUM — the four weeks */}
      <section className="band" style={{ background: ALT }} data-testid="modules">
        <div className="wrap">
          <Eyebrow>{c.modules.eyebrow}</Eyebrow>
          <h2 className="display">{c.modules.h2}</h2>
          {c.modules.intro && <p className="sub">{c.modules.intro}</p>}
          <ul className="mods">
            {c.modules.items.map((m) => (
              <li key={m.title}>
                <p className="mt"><span className="star" aria-hidden="true">✦</span>{m.title}</p>
                {m.journey && <p className="mj">Official journey: <b>{m.journey}</b></p>}
                <p className="md">{m.desc}</p>
              </li>
            ))}
          </ul>
          {c.modules.footnote && <p className="cite" data-testid="modules-footnote">{c.modules.footnote}</p>}
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="band" style={{ background: BG }} data-testid="included">
        <div className="wrap">
          <Eyebrow>{inc.eyebrow}</Eyebrow>
          <h2 className="display">{inc.h2}</h2>
          <ul className="checks">
            {inc.items.map((b) => <li key={b}><span className="chk" aria-hidden="true">✓</span>{b}</li>)}
          </ul>
          {inc.note && <p className="cite">{inc.note}</p>}
        </div>
      </section>

      {/* PRICING */}
      <section className="band center" style={{ background: ALT }} id="enroll">
        <div className="wrap narrow">
          <ValueStack
            eyebrow={vs.eyebrow}
            mainItem={vs.main_item}
            yourPrice={price}
            priceLabel={`${P.discount_label} price`}
            priceCaption={vs.price_caption}
            pricingNote={vs.pricing_note}
            ctaSlot={<CTA label={vs.cta} testId="cta-stack" />}
          />
        </div>
      </section>

      {/* ENROLLMENT PROCESS + REQUEST FORM */}
      <section className="band" style={{ background: BG }} data-testid="enrollment">
        <div className="wrap enroll-grid">
          <div>
            <Eyebrow>{en.eyebrow}</Eyebrow>
            <h2 className="display">{en.h2}</h2>
            <ol className="steps" data-testid="enrollment-steps">
              {en.steps.map((s, i) => (
                <li key={s.title}>
                  <span className="step-n">{i + 1}</span>
                  <div><p className="mt">{s.title}</p><p className="md">{s.desc}</p></div>
                </li>
              ))}
            </ol>
          </div>
          <RequestForm title={en.form_title} note={en.form_note} arm={arm} />
        </div>
      </section>

      {/* SINGLE JOURNEY — choose your date */}
      {sj && (
        <section className="band" style={{ background: ALT }} data-testid="single-journey-section">
          <div className="wrap enroll-grid">
            <div>
              <Eyebrow>{sj.eyebrow}</Eyebrow>
              <h2 className="display">{sj.h2}</h2>
              <Paras text={sj.body} />
              <ol className="steps" data-testid="single-journey-steps">
                {sj.steps.map((s, i) => (
                  <li key={s.title}>
                    <span className="step-n">{i + 1}</span>
                    <div><p className="mt">{s.title}</p><p className="md">{s.desc}</p></div>
                  </li>
                ))}
              </ol>
            </div>
            <RequestForm id="single-journey" offer="single-journey" dates={sj.dates} title={sj.form_title} note={sj.form_note} submitLabel="Choose Your Date" arm={arm} />
          </div>
        </section>
      )}

      {/* PROOF */}
      <Testimonials proof={c.proof} sanitized={isAds} />

      {/* LIVE EVENT */}
      {le?.enabled && (
        <section className="band" style={{ background: ALT }} data-testid="live-event">
          <div className="wrap">
            <p className="live"><span className="dot" aria-hidden="true" />{le.label}</p>
            <h2 className="display">{le.h2}</h2>
            <p className="when">{le.date_display}</p>
            <div className="body">
              <p>{le.body}</p>
              {le.replay_note && <p className="muted">{le.replay_note}</p>}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="band" style={{ background: ALT }}>
        <div className="wrap narrow" data-testid="faq">
          <Eyebrow>Questions</Eyebrow>
          <h2 className="display">Before you request a spot.</h2>
          <div className="faq">
            {c.faq.map((f, i) => {
              const open = openFaq === i;
              return (
                <div className="qa" key={f.q}>
                  <button type="button" id={`faq-${i}`} aria-expanded={open} aria-controls={`faq-a-${i}`}
                          onClick={() => setOpenFaq(open ? null : i)}>
                    <span>{f.q}</span><span className="pm" aria-hidden="true">{open ? '−' : '+'}</span>
                  </button>
                  {open && (
                    <div className="ans" id={`faq-a-${i}`} role="region" aria-labelledby={`faq-${i}`}>
                      <p>{f.a}</p>
                      {f.link && <p><a href={f.link.url} target="_blank" rel="noopener noreferrer">{f.link.label} →</a></p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA — text only, centered */}
      <section className="band center final" style={{ background: BG }}>
        <div className="wrap narrow">
          <h2 className="display">{fc.h2}</h2>
          <p className="sub">{fc.body}</p>
          <div className="cta-wrap c"><CTA label={fc.cta} testId="cta-final" /></div>
        </div>
      </section>

      <LegalFooter footer={c.footer} extended={isAds} />
    </div>
  );
}
